import React, { useContext } from "react"
import { Area, Bar, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"

import type { GetBioacousticsSampleDataQuery } from "@/__generated__/graphql"
import { myhiveLocalization, MyHiveLocalizationKey } from '@/localization/my-hive.js';
import { LanguageContext } from '@/providers/LanguageProvider.js';

import MiniSpinner from '../../asset/components/mini-spinner.js'

type BioacousticsData = GetBioacousticsSampleDataQuery['getBioacousticsSampleData'];

export type BioacousticsMetric = 'buzz' | 'richness' | 'birds';
export type BioacousticsChartType = 'line' | 'bar';

export interface ChartRow {
  date: string;
  meanBuzzPerHour: number | null | undefined;
  observations: number;
  richness: number;
  detections: number;
  meanTemp: number | null | undefined;
  totalPrecip: number | null | undefined;
}

// Invisible tooltip content that fires onHoverRow whenever Recharts changes the active point.
// Using a component (not a plain function) so we can use useEffect to defer the setState call
// out of the render cycle and avoid the "setState during render" warning.
function HoverTracker({
  active,
  payload,
  onHoverRow,
}: {
  active?: boolean;
  payload?: ReadonlyArray<{ payload: ChartRow }>;
  onHoverRow: (row: ChartRow | null) => void;
}): null {
  const row: ChartRow | null = active && payload?.[0]?.payload ? payload[0].payload : null;
  React.useEffect(() => {
    onHoverRow(row);
  }, [row?.date ?? null]);
  return null;
}

export function buildChartData(data: BioacousticsData): ChartRow[] {
  const countsByDay: Record<string, number> = {};
  const hzByDay: Record<string, Set<string>> = {};
  for (const obs of data.hourlyPollinatorObservations) {
    const day = obs.date;
    countsByDay[day] = (countsByDay[day] ?? 0) + Number(obs.count);
    (hzByDay[day] ??= new Set()).add(String(obs.hz));
  }

  const birdCountsByDay: Record<string, number> = {};
  for (const obs of data.birdObservations) {
    const day = String(obs.dateTime).substring(0, 10);
    birdCountsByDay[day] = (birdCountsByDay[day] ?? 0) + 1;
  }

  interface DailyEntry { date: string; meanBuzzPerHour: number | null; weatherData: { meanTemp: number | null; totalPrecip: number | null } }
  const rows = (data.dailyPollinatorData as DailyEntry[]).map(({ date, meanBuzzPerHour, weatherData }) => {
    const dateKey = date.substring(0, 10);
    return {
      date: dateKey,
      meanBuzzPerHour,
      meanTemp: weatherData.meanTemp,
      totalPrecip: weatherData.totalPrecip,
      observations: countsByDay[dateKey] ?? 0,
      rawRichness: hzByDay[dateKey]?.size ?? 0,
      detections: birdCountsByDay[dateKey] ?? 0,
    };
  });

  return rows.map((row) => ({
    date: row.date,
    meanBuzzPerHour: row.meanBuzzPerHour,
    observations: row.observations,
    richness: row.rawRichness,
    detections: row.detections,
    meanTemp: row.meanTemp,
    totalPrecip: row.totalPrecip,
  }));
}

export function BioacousticsDailyChart(params: {
  data?: BioacousticsData;
  metric: BioacousticsMetric;
  chartType?: BioacousticsChartType;
  showWeather?: boolean;
  showTrend?: boolean;
  startDate?: string;
  endDate?: string;
  onHoverRow?: (row: ChartRow | null) => void;
  syncId?: string;
  showTooltip?: boolean;
}): React.JSX.Element {
  const { data, metric, chartType = 'line', showWeather = true, showTrend = false, startDate, endDate, onHoverRow, syncId, showTooltip = true } = params;
  const { language } = useContext(LanguageContext);
  const t = myhiveLocalization[language];
  if (!data) {
    return <div className="w-full h-full flex items-center justify-center"><MiniSpinner /></div>;
  }

  const filtered = buildChartData(data).filter(row => {
    if (startDate && row.date < startDate) return false;
    if (endDate && row.date > endDate) return false;
    return true;
  });
  const lastNonNull = filtered.reduce((last, row, i) => row.meanBuzzPerHour != null ? i : last, -1);
  const baseChartData = lastNonNull >= 0 ? filtered.slice(0, lastNonNull + 1) : filtered;

  // Compute linear trend when requested
  type ChartRowWithTrend = ChartRow & { trend?: number };
  let chartData: ChartRowWithTrend[] = baseChartData;
  if (showTrend && chartType === 'line') {
    const points = baseChartData
      .map((row, i) => row.meanBuzzPerHour != null ? { x: i, y: row.meanBuzzPerHour } : null)
      .filter((p): p is { x: number; y: number } => p !== null);
    if (points.length >= 2) {
      const n = points.length;
      const sumX = points.reduce((s, p) => s + p.x, 0);
      const sumY = points.reduce((s, p) => s + p.y, 0);
      const sumXY = points.reduce((s, p) => s + p.x * p.y, 0);
      const sumX2 = points.reduce((s, p) => s + p.x * p.x, 0);
      const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
      const intercept = (sumY - slope * sumX) / n;
      chartData = baseChartData.map((row, i) => ({ ...row, trend: Math.max(0, slope * i + intercept) }));
    }
  }

  const todayStr = new Date().toISOString().substring(0, 10);
  const yesterdayStr = new Date(Date.now() - 86_400_000).toISOString().substring(0, 10);
  const chartDates = new Set(chartData.map(r => r.date));
  const refDate = chartDates.has(todayStr) ? todayStr : yesterdayStr;
  const refLabel = chartDates.has(todayStr) ? t[MyHiveLocalizationKey.BioacousticsChartToday] : t[MyHiveLocalizationKey.BioacousticsChartYesterday];
  const oneWeekAgo = new Date(new Date(refDate + 'T00:00:00Z').getTime() - 7 * 86_400_000).toISOString().substring(0, 10);
  const twoWeeksAgo = new Date(new Date(refDate + 'T00:00:00Z').getTime() - 14 * 86_400_000).toISOString().substring(0, 10);
  const xLabelMap: Record<string, string> = {
    [refDate]: refLabel,
    [oneWeekAgo]: t[MyHiveLocalizationKey.BioacousticsChart1WkAgo],
    [twoWeeksAgo]: t[MyHiveLocalizationKey.BioacousticsChart2WksAgo],
  };

  const metricColor =
    metric === 'richness' ? 'var(--color-brand-teal-500)'
    : metric === 'birds'   ? 'var(--color-brand-ruby-400)'
    :                        'var(--color-brand-warm-orange-500)';

  const metricKey =
    metric === 'richness' ? 'richness'
    : metric === 'birds'   ? 'detections'
    :                        'meanBuzzPerHour';

  const metricName =
    metric === 'richness' ? 'Unique Hz/Day'
    : metric === 'birds'   ? 'Detections/day'
    :                        'Detections/Hr';

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart
        data={chartData}
        margin={{ top: 8, bottom: 8, right: 16, left: 0 }}
        syncId={syncId}
      >
        <XAxis dataKey="date" scale={chartType === 'bar' ? 'point' : undefined} padding={{ left: 16, right: 16 }} tick={{ fontSize: 11 }} tickLine={true} tickFormatter={(v: string) => xLabelMap[v] ?? ''} interval={0} />
        <YAxis yAxisId="main" width={36} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} domain={[0, (dataMax: number) => Math.max(1, Math.ceil(dataMax * 1.15))]} />
        <YAxis yAxisId="temp" hide width={0} />
        <YAxis yAxisId="precip" hide width={0} />
        <Tooltip
          content={onHoverRow ? (props: { active?: boolean; payload?: ReadonlyArray<{ payload: ChartRow }> }) => (
            <HoverTracker
              active={props.active}
              payload={props.payload}
              onHoverRow={onHoverRow}
            />
          ) : undefined}
          formatter={!onHoverRow ? (v: number | undefined, name: string | undefined) => [v?.toFixed(metric === 'birds' ? 0 : 2) ?? '—', name ?? ''] : undefined}
          wrapperStyle={showTooltip ? {} : { opacity: 0, pointerEvents: 'none' }}
          cursor={{ stroke: '#d1d5db', strokeWidth: 1 }}
        />
        {showWeather && <Legend wrapperStyle={{ paddingLeft: '36px' }} layout="horizontal" verticalAlign="bottom" align="center" />}
        {chartType === 'bar' ? (
          <Bar
            yAxisId="main"
            dataKey={metricKey}
            name={metricName}
            fill={metricColor}
            isAnimationActive={false}
            maxBarSize={16}
            legendType="none"
          />
        ) : (
          <>
            <Line
              yAxisId="main"
              type="monotone"
              dataKey={metricKey}
              name={metricName}
              isAnimationActive={false}
              dot={{ strokeWidth: 1 }}
              strokeWidth={3}
              stroke={metricColor}
              connectNulls={metric !== 'richness'}
              legendType="none"
            />
            {showTrend && (
              <Line
                yAxisId="main"
                type="linear"
                dataKey="trend"
                name="Trend"
                isAnimationActive={false}
                dot={false}
                activeDot={false}
                strokeWidth={1.5}
                stroke={metricColor}
                strokeOpacity={0.5}
                strokeDasharray="6 3"
                legendType="none"
              />
            )}
          </>
        )}
        {showWeather && (
          <Line yAxisId="precip" type="monotone" dataKey="totalPrecip" name="Precip (mm)" dot={false} strokeWidth={1.5} stroke="var(--color-brand-sky-blue-500)" strokeOpacity={0.5} connectNulls />
        )}
        {showWeather && (
          <Area yAxisId="temp" type="monotone" dataKey="meanTemp" name="Temp (°C)" dot={false} strokeWidth={1.5} stroke="var(--color-brand-ruby-400)" strokeOpacity={0.6} fill="var(--color-brand-ruby-400)" fillOpacity={0.12} baseValue="dataMin" connectNulls />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
}
